import { useEffect, useState } from "react";
import { Form, FormInput, FormActions, FormTextArea, FormDate } from "../Controls/Form";
import TreeView from "../Controls/TreeView";
import SkillSearch from "../Controls/SkillSearch";
import API from "@src/API";
import { toast } from "react-toastify";

async function loadCategories() {
  const resp = await API.getAsync("/Skill/Categories");
  if (resp?.ok && Array.isArray(resp.jsonData)) {
    return resp.jsonData.map((node) => ({
      ID: node.ID ?? node.id,
      Parent: node.Parent ?? node.parent ?? 0,
      Name: node.Name ?? node.name ?? "",
    }));
  }
  return [];
}

function emptySkill(name = "") {
  return {
    ID: 0,
    ParentID: 0,
    ParentSkillName: "",
    Name: name || "",
    ShortName: "",
    CategoryID: 0,
    CategoryName: "",
    Description: "",
    ReleaseDate: null,
    Version: "",
    WikiUrl: "",
    Status: "pending",
  };
}

function normalizeSkill(raw, fallbackName = "") {
  if (!raw || typeof raw !== "object") return emptySkill(fallbackName);
  return {
    ...emptySkill(fallbackName),
    ID: raw.ID ?? raw.id ?? 0,
    ParentID: raw.ParentID ?? raw.parentID ?? 0,
    ParentSkillName: raw.ParentSkillName ?? raw.parentSkillName ?? "",
    Name: raw.Name ?? raw.name ?? fallbackName ?? "",
    ShortName: raw.ShortName ?? raw.shortName ?? "",
    CategoryID: raw.CategoryID ?? raw.categoryID ?? 0,
    CategoryName: raw.CategoryName ?? raw.categoryName ?? "",
    Description: raw.Description ?? raw.description ?? "",
    ReleaseDate: raw.ReleaseDate ?? raw.releaseDate ?? null,
    Version: raw.Version ?? raw.version ?? "",
    WikiUrl: raw.WikiUrl ?? raw.wikiUrl ?? "",
    Status: raw.Status ?? raw.status ?? "pending",
  };
}

/**
 * Skill form body. Prefer SkillEditModal for UI entry points.
 */
export default function SkillEdit({ skillId = 0, initialName = "", onClose }) {
  const id = Number.parseInt(skillId, 10) || 0;
  const isCreate = !(id > 0);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("info");
  const [skill, updateSkill] = useState(() => emptySkill(initialName));
  const [hydrated, setHydrated] = useState(isCreate);

  useEffect(() => {
    let cancelled = false;
    setHydrated(isCreate);
    if (!id) {
      updateSkill(emptySkill(initialName));
      setHydrated(true);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      const resp = await API.getAsync("/Skill?ID=" + id);
      if (cancelled) return;
      if (resp?.ok && resp.jsonData) {
        const raw = Array.isArray(resp.jsonData) ? resp.jsonData[0] : resp.jsonData;
        updateSkill(normalizeSkill(raw));
        setHydrated(true);
      } else {
        toast.error("Could not load skill");
        setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, initialName, isCreate]);

  function close(result) {
    onClose?.(result);
  }

  async function onDoneAsync() {
    if (busy) return;
    if (!(skill.Name || "").trim()) {
      toast.error("Skill name is required");
      setTab("info");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...skill,
        ID: skill.ID || id || 0,
        ParentID: skill.ParentID > 0 ? skill.ParentID : null,
        Name: (skill.Name || "").trim(),
        ShortName: (skill.ShortName || skill.Name || "").trim(),
      };
      const resp = await API.postAsync("/Skill", payload);
      if (resp?.ok) {
        toast.success("Skill saved");
        const raw = Array.isArray(resp.jsonData)
          ? resp.jsonData[resp.jsonData.length - 1]
          : resp.jsonData;
        const saved = normalizeSkill(raw, payload.Name);
        if (!saved.ID && raw) {
          saved.ID = raw.ID ?? raw.id ?? 0;
        }
        onClose?.(saved);
      } else {
        toast.error(resp?.jsonData?.message || "Save failed");
      }
    } finally {
      setBusy(false);
    }
  }

  function onParentSelect(selected) {
    if (!selected) return;
    const parentId = selected.ID ?? selected.id ?? 0;
    const name = selected.Name ?? selected.name ?? "";
    updateSkill((prev) => ({
      ...prev,
      ParentID: parentId,
      ParentSkillName: name,
    }));
  }

  if (!hydrated) {
    return (
      <div className="cm-modal-embedded" data-testid="skill-edit-loading">
        <p className="fieldHint">Loading skill…</p>
      </div>
    );
  }

  return (
    <div className="cm-modal-embedded">
      <Form
        key={hydrated ? `skill-${id || "new"}-ready` : `skill-${id || "new"}-loading`}
        className="cm-modal-form"
        object={skill}
        onUpdate={updateSkill}
      >
        <div className="cm-modal-tabs" role="tablist" aria-label="Skill sections">
          <button
            type="button"
            role="tab"
            id="skill-tab-info"
            aria-selected={tab === "info"}
            aria-controls="skill-panel-info"
            className={tab === "info" ? "is-active" : undefined}
            data-testid="skill-tab-info"
            onClick={() => setTab("info")}
          >
            Info
          </button>
          <button
            type="button"
            role="tab"
            id="skill-tab-details"
            aria-selected={tab === "details"}
            aria-controls="skill-panel-details"
            className={tab === "details" ? "is-active" : undefined}
            data-testid="skill-tab-details"
            onClick={() => setTab("details")}
          >
            Details
          </button>
          <button
            type="button"
            role="tab"
            id="skill-tab-context"
            aria-selected={tab === "context"}
            aria-controls="skill-panel-context"
            className={tab === "context" ? "is-active" : undefined}
            data-testid="skill-tab-context"
            onClick={() => setTab("context")}
          >
            Context
          </button>
        </div>

        <div className="cm-modal-tab-panels">
          <div
            id="skill-panel-info"
            role="tabpanel"
            aria-labelledby="skill-tab-info"
            aria-hidden={tab !== "info"}
            className={`cm-modal-tab-panel${tab === "info" ? " is-active" : ""}`}
            {...(tab !== "info" ? { inert: "" } : {})}
          >
            <FormInput field="Name" label="Name" />
            <FormInput field="ShortName" label="Short Name" />
            <FormTextArea field="Description" label="Description" />
            <TreeView
              field="CategoryID"
              fieldDisplay="CategoryName"
              label="Category"
              getData={loadCategories}
              formObject={{
                object: skill,
                onChange: (value, field) => {
                  updateSkill((prev) => ({ ...prev, [field]: value }));
                },
              }}
            />
          </div>

          <div
            id="skill-panel-details"
            role="tabpanel"
            aria-labelledby="skill-tab-details"
            aria-hidden={tab !== "details"}
            className={`cm-modal-tab-panel${tab === "details" ? " is-active" : ""}`}
            {...(tab !== "details" ? { inert: "" } : {})}
          >
            <FormInput field="Version" label="Version" />
            <FormDate field="ReleaseDate" label="Release Date" />
            <FormInput field="Status" label="Status" disabled />
          </div>

          <div
            id="skill-panel-context"
            role="tabpanel"
            aria-labelledby="skill-tab-context"
            aria-hidden={tab !== "context"}
            className={`cm-modal-tab-panel${tab === "context" ? " is-active" : ""}`}
            {...(tab !== "context" ? { inert: "" } : {})}
          >
            <div className="field">
              <label className="form-label">Parent Skill</label>
              <SkillSearch
                object={skill}
                field="ParentID"
                fieldDisplay="ParentSkillName"
                allowCreate={false}
                onSelect={onParentSelect}
              />
            </div>
          </div>
        </div>

        <FormActions
          doneLabel={busy ? "Saving…" : "Save"}
          onSaveAsync={async () => {
            await onDoneAsync();
          }}
          onCancelAsync={async () => {
            close();
          }}
        />
      </Form>
    </div>
  );
}
